import { call, put, select } from "redux-saga/effects";
import { AlertStatusMessage, IApiError, IClientCompany, ToggleActionType } from '@/shared/types';
import { IGetManyClientCompaniesParams } from "../model/types/interfaces";
import { selectParams } from "../model/selectors";
import {
  getCreateDtoByCarrierFormData,
  getCreateDtoByShippingLocationFormData, getUpdateDtoByCarrierFormData,
  getUpdateDtoByShippingLocationFormData,
  modifyClientCompaniesData,
} from './helpers';
import {
  getClientCompany,
  updateClientCompany,
  deleteClientCompany,
  createClientCompany,
  getManyClientCompanies,
  getManyShippingLocations,
  getManyCarriers,
  updateShippingLocation,
  createShippingLocation,
  deleteShippingLocation,
  updateCarrier,
  createCarrier,
  deleteCarrier,
} from './senders';
import { commonSlice } from "@/entities/common";
import { clientCompaniesSlice } from "../model/slice";
import { PayloadAction } from '@reduxjs/toolkit';
import { asideSagaActions, asideSlice } from '@/widgets/side-block';
import { sagaActions } from './actions';
import {
  CreateClientCompanyPayload,
  DeleteClientCompanyPayload,
  GetClientCompanyResponse,
  GetCompanyPayload,
  GetShippingLocationsPayload,
  UpdateClientCompanyPayload,
  UpdateShippingLocationPayload,
  CreateShippingLocationPayload, DeleteShippingLocationPayload, UpdateCarrierPayload, CreateCarrierPayload,
  DeleteCarrierPayload,
} from '@/entities/client-companies/api/types/types';
import { alertsSagaActions } from '@/entities/alerts';
import { getUpdateDtoByFormData } from '@/entities/vendors/api/helpers';
import { t } from 'i18next';
import { dialogSagaActions } from '@/widgets/dialog';
import { useClientCompaniesActions } from '@/entities/client-companies';

export function* handleRetrieveManyShippingLocations({
                                                       payload,
                                                     }: PayloadAction<GetShippingLocationsPayload>) {
  const { setShippingLocations } = clientCompaniesSlice;
  const { setLoading } = commonSlice;
  const { clientCompanyId } = payload;
  const baseBody: IGetManyClientCompaniesParams = yield select(selectParams);

  const body: IGetManyClientCompaniesParams = clientCompanyId
    ? { ...baseBody, clientCompanyId }
    : baseBody;

  try {
    yield put(setLoading({ isLoading: true }));

    const { items, count } = yield call(getManyShippingLocations, body);
    const modifiedClientCompanies: Array<IClientCompany> = yield call(
      modifyClientCompaniesData,
      items
    );

    yield put(
      setShippingLocations({
        shippingLocations: { items: modifiedClientCompanies, count },
      })
    );
  } catch (err) {
    console.error("handleRetrieveManyShippingLocations error:", err);
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

export function* handleRetrieveManyCarriers({
                                                       payload,
                                                     }: PayloadAction<GetShippingLocationsPayload>) {
  const { setCarriers } = clientCompaniesSlice;
  const { setLoading } = commonSlice;
  const { clientCompanyId } = payload;
  const baseBody: IGetManyClientCompaniesParams = yield select(selectParams);

  const body: IGetManyClientCompaniesParams = clientCompanyId
    ? { ...baseBody, clientCompanyId }
    : baseBody;

  try {
    yield put(setLoading({ isLoading: true }));

    const { items, count } = yield call(getManyCarriers, body);
    const modifiedClientCompanies: Array<IClientCompany> = yield call(
      modifyClientCompaniesData,
      items
    );

    yield put(
      setCarriers({
        carriers: { items: modifiedClientCompanies, count },
      })
    );
  } catch (err) {
    console.error("handleRetrieveManyCarriers error:", err);
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

export function* handleRetrieveManyCompanies() {
  const { setClientCompanies } = clientCompaniesSlice;
  const { setLoading } = commonSlice;
  const body: IGetManyClientCompaniesParams = yield select(selectParams);

  yield put(setLoading({ isLoading: true }));

  const { items, count } = yield call(getManyClientCompanies, body);
  const modifiedClientCompanies: Array<IClientCompany> = yield call(
    modifyClientCompaniesData,
    items
  );


  yield put(setClientCompanies({ clientCompanies: { items: modifiedClientCompanies, count } }));
  yield put(setLoading({ isLoading: false }));
}

export function* handleRetrieveCompany({ payload }: PayloadAction<GetCompanyPayload>) {
  const { companyId } = payload;
  const { setActiveEntity } = commonSlice;
  const { setLoadingAside } = asideSlice;

  const response: GetClientCompanyResponse = yield call(getClientCompany, companyId);

  yield put(setActiveEntity({ activeEntity: response.data }));
  yield put(setLoadingAside({ isLoading: false }));
}

export function* handleUpdateCompany(
  action: PayloadAction<UpdateClientCompanyPayload>
) {
  const { clientCompanyId, formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleSideBlock } = asideSagaActions;

  const updateDto: Partial<IClientCompany> = yield call(
    getUpdateDtoByFormData,
    formData
  );

  try {
    yield call(updateClientCompany, clientCompanyId, updateDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("company_updated_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleSideBlock({ status: ToggleActionType.CLOSE }));
    yield call(handleRetrieveManyCompanies);
  }
}

export function* handleUpdateShippingLocation(
  action: PayloadAction<UpdateShippingLocationPayload>
) {
  const { shippingLocationId, formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleSideBlock } = asideSagaActions;
  const { retrieveManyShippingLocations } = sagaActions;

  const updateDto: Partial<IClientCompany> = yield call(
    getUpdateDtoByShippingLocationFormData,
    formData
  );

  try {
    yield call(updateShippingLocation, shippingLocationId, updateDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("shipping_location_updated_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(retrieveManyShippingLocations({ clientCompanyId: formData.clientCompanyId }));
  }
}

export function* handleUpdateCarrier(
  action: PayloadAction<UpdateCarrierPayload>
) {
  const { shippingLocationId, formData } = action.payload;
  console.log('444444444', formData)
  const { updateAlert } = alertsSagaActions;
  const { toggleSideBlock } = asideSagaActions;
  const { retrieveManyCarriers } = sagaActions;

  const updateDto: Partial<IClientCompany> = yield call(
    getUpdateDtoByCarrierFormData,
    formData
  );

  try {
    yield call(updateCarrier, shippingLocationId, updateDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("carrier_updated_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(retrieveManyCarriers({ clientCompanyId: formData.clientCompanyId }));
  }
}

export function* handleCreateCompany(
  action: PayloadAction<CreateClientCompanyPayload>
) {
  const { formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleSideBlock } = asideSagaActions;

  const updateDto: Partial<IClientCompany> = yield call(
    getUpdateDtoByFormData,
    formData
  );

  try {
    yield call(createClientCompany, updateDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("company_created_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleSideBlock({ status: ToggleActionType.CLOSE }));
    yield call(handleRetrieveManyCompanies);
  }
}

export function* handleCreateShippingLocation(
  action: PayloadAction<CreateShippingLocationPayload>
) {
  const { formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { retrieveManyShippingLocations } = sagaActions;

  const updateDto: Partial<IClientCompany> = yield call(
    getCreateDtoByShippingLocationFormData,
    formData
  );

  try {
    yield call(createShippingLocation, updateDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("shipping_location_created_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield put(retrieveManyShippingLocations({ clientCompanyId: formData.clientCompanyId }));
  }
}

export function* handleCreateCarrier(
  action: PayloadAction<CreateCarrierPayload>
) {
  const { formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { retrieveManyCarriers } = sagaActions;

  const updateDto: Partial<IClientCompany> = yield call(
    getCreateDtoByCarrierFormData,
    formData
  );

  try {
    yield call(createCarrier, updateDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("carrier_created_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield put(retrieveManyCarriers({ clientCompanyId: formData.clientCompanyId }));
  }
}

export function* handleDeleteCompany(
  action: PayloadAction<DeleteClientCompanyPayload>
) {
  const { clientCompanyId } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;

  try {
    yield call(deleteClientCompany, clientCompanyId);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("company_deleted_successfully")}`,
      })
    );
  } catch (err) {
    yield put(
      updateAlert({ status: AlertStatusMessage.ERROR, message: `${t("company_delete_error")}` })
    );
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield call(handleRetrieveManyCompanies);
  }
}

export function* handleDeleteShippingLocation(
  action: PayloadAction<DeleteShippingLocationPayload>
) {
  const { shippingLocationId } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;

  try {
    yield call(deleteShippingLocation, shippingLocationId);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("location_deleted_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield call(handleRetrieveManyCompanies);
  }
}

export function* handleDeleteCarrier(
  action: PayloadAction<DeleteCarrierPayload>
) {
  const { shippingLocationId, clientCompanyId } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { retrieveManyCarriers } = sagaActions;

  try {
    yield call(deleteCarrier, shippingLocationId);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("carrier_deleted_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE, isntEntityClear: true }));
    yield put(retrieveManyCarriers({ clientCompanyId: clientCompanyId }));
  }
}

function* handleError(err: any) {
  const { updateAlert } = alertsSagaActions;
  const error = err as IApiError;

  if (error?.response?.data?.error?.message?.length) {
    for (const text of error.response.data.error.message) {
      yield put(
        updateAlert({ status: AlertStatusMessage.ERROR, message: text })
      );
    }
  }
}
